type AuthResponseLocation = Pick<Location, "hash" | "search">;

const MSAL_INTERACTION_TYPES = new Set(["popup", "redirect", "silent"]);

interface MsalLibraryState {
  id?: unknown;
  meta?: {
    interactionType?: unknown;
  };
}

function decodeMsalLibraryState(state: string): MsalLibraryState | null {
  try {
    const libraryState = state.split("|")[0];
    const normalized = libraryState.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as MsalLibraryState;
  } catch {
    return null;
  }
}

/**
 * MSAL Browser v5 returns popup, redirect, and silent responses to the
 * configured redirect URI and expects that page to run its redirect bridge.
 * Detect only MSAL-owned state so other OAuth callbacks can keep booting the
 * regular application.
 */
export function isMsalAuthResponse(location: AuthResponseLocation): boolean {
  for (const rawParams of [location.hash, location.search]) {
    if (!rawParams || rawParams.length < 2) continue;

    const params = new URLSearchParams(rawParams.slice(1));
    const state = params.get("state");
    if (!state) continue;

    const libraryState = decodeMsalLibraryState(state);
    const interactionType = libraryState?.meta?.interactionType;
    if (
      typeof libraryState?.id === "string" &&
      libraryState.id.length > 0 &&
      typeof interactionType === "string" &&
      MSAL_INTERACTION_TYPES.has(interactionType)
    ) {
      return true;
    }
  }

  return false;
}

export async function broadcastMsalAuthResponse(): Promise<void> {
  const { broadcastResponseToMainFrame } = await import(
    "@azure/msal-browser/redirect-bridge"
  );
  await broadcastResponseToMainFrame();
}
